/**
 * Force sync — re-download ONE template that is on this device: its full record, then a FORCED
 * download of every saved view's mock-up (a re-bake under the same version id is otherwise
 * invisible here). Templates ▸ Force sync, Mock-ups ▸ Sync and "Download missing" all run this,
 * so the three cannot drift.
 */
import type { PlatformIO, ProductTemplate } from "@genz/platform";
import { hydrateBakedAssets, ioHydrateDeps, hasAnyBakedView, type BakedAssetRefsMap } from "./bakedSync.js";
import { syncOneTemplate, type ReconcileClient } from "./syncedReconcile.js";
import { hydrationFailure, useHydrationStore } from "../stores/hydrationStore.js";
import { hasMockupStored } from "../stores/mockupStore.js";
import { getPrimaryViewFallback } from "../stores/renderFallback.js";
import { PRIMARY_VIEW } from "./views.js";

export type ForceSyncIO = Pick<PlatformIO, "localStore"> & Parameters<typeof ioHydrateDeps>[0];

export interface ForceSyncResult {
  template: ProductTemplate;
  /** False when the cloud version publishes no mock-up: the record synced, nothing to download. */
  hasBake: boolean;
}

/** The view types a version saved, deduped, in order — `["back"]` when it defines none. */
export function viewTypes(views: readonly { view_type?: string | null }[] | null | undefined): string[] {
  const unique = [...new Set((views ?? []).map((v) => v.view_type).filter((v): v is string => !!v))];
  return unique.length > 0 ? unique : [PRIMARY_VIEW];
}

export function savedViews(t: ProductTemplate): string[] {
  return viewTypes(t.currentVersion?.payload?.views);
}

/** Throws when the record or any view's mock-up could not be downloaded. */
export async function forceSyncTemplate(
  io: ForceSyncIO,
  client: ReconcileClient,
  templateId: string,
): Promise<ForceSyncResult> {
  const template = await syncOneTemplate(io, client, templateId);
  const versionId = template.currentVersion?.versionId;
  const refs = template.currentVersion?.bakedAssets as BakedAssetRefsMap | null | undefined;
  if (!versionId || !hasAnyBakedView(refs)) return { template, hasBake: false };
  const ok = await hydrateBakedAssets(versionId, refs, ioHydrateDeps(io), {
    views: savedViews(template), force: true,
  });
  if (!ok) throw new Error(hydrationFailure(versionId)?.message ?? "the mock-up download did not finish");
  return { template, hasBake: true };
}

/** A staged template, as far as the pre-run mock-up check needs to know it. */
export interface MockupTarget {
  templateId: string;
  versionId: string;
  name: string;
  views: readonly string[];
  /** The published version declares a baked mock-up (`TemplateSelection.bakeExpected`). */
  bakeExpected?: boolean;
}

/**
 * The targets the queue would refuse for want of a mock-up on this device. A failed download
 * counts; one still running does not. A template that declares no bake may render classic, so
 * it counts only when its download failed.
 */
export async function findMissingMockups<T extends MockupTarget>(targets: readonly T[]): Promise<T[]> {
  const missing = await Promise.all(targets.map(isMissing));
  return targets.filter((_, i) => missing[i]);
}

async function isMissing(t: MockupTarget): Promise<boolean> {
  if (!t.versionId) return false;
  if (useHydrationStore.getState().status[t.versionId]?.state === "hydrating") return false;
  if (hydrationFailure(t.versionId)) return true;
  if (t.bakeExpected !== true) return false;
  // With the primary-view fallback on, the queue renders any view over the primary's shell.
  if (getPrimaryViewFallback() && await hasMockupStored(t.versionId, PRIMARY_VIEW)) return false;
  const stored = await Promise.all(t.views.map((v) => hasMockupStored(t.versionId, v)));
  return !stored.every(Boolean);
}
